import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { mapRoutes } from '@/routes/routes';
import {
  isOverlayPath,
  type BackgroundLocationState,
} from '@/hooks/useBackgroundLocation';

import {
  type ArtifactDefinition,
  type ArtifactId,
  type ViewMode,
} from './registry';

// React Router location.key for the swapped backgroundLocation when the
// user changes artifact while an overlay is open. Stable key lets router
// internals dedupe history entries.
const ARTIFACT_SWITCH_LOCATION_KEY = 'artifact-switch';

/**
 * Build a background path for the given artifact that preserves the
 * current `?view=` preference. Falls back to the artifact's `defaultView`
 * when the current view is not supported by the artifact (e.g. switching
 * from a tasks kanban view to notes, which do not support kanban).
 */
function buildBackgroundPath(
  artifact: ArtifactDefinition,
  shortId: string,
  currentView: string | null
): string {
  const base = mapRoutes.background(artifact.id as ArtifactId, shortId);
  if (!currentView) return base;

  const supported = artifact.supportedViews as readonly ViewMode[];
  const targetView = supported.includes(currentView as ViewMode)
    ? currentView
    : artifact.defaultView;
  return `${base}?view=${targetView}`;
}

interface UseArtifactSwitchReturn {
  /** Navigate to the given artifact preserving `?view=` (with defaultView fallback). */
  navigateToArtifact: (artifact: ArtifactDefinition) => void;
  /** Build the path the switcher would navigate to (for `<a href>` accessibility). */
  buildHref: (artifact: ArtifactDefinition) => string;
}

/**
 * Shared navigation logic for artifact switchers (rail bar inside SidePanel,
 * dropdown in TopBar, etc.). Preserves the current `?view=` and handles the
 * overlay-open case by swapping the stored backgroundLocation so the panel
 * underneath updates without closing the overlay.
 */
export function useArtifactSwitch(): UseArtifactSwitchReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortId } = useParams<{ shortId: string }>();

  const buildHref = useCallback(
    (artifact: ArtifactDefinition): string => {
      const params = new URLSearchParams(location.search);
      const currentView = params.get('view');
      return buildBackgroundPath(artifact, shortId ?? '', currentView);
    },
    [location.search, shortId]
  );

  const navigateToArtifact = useCallback(
    (artifact: ArtifactDefinition) => {
      const fullPath = buildHref(artifact);
      const queryIndex = fullPath.indexOf('?');
      const pathname =
        queryIndex === -1 ? fullPath : fullPath.slice(0, queryIndex);
      const search = queryIndex === -1 ? '' : fullPath.slice(queryIndex);

      // Inside an overlay (e.g. /map/task/:id) — keep the overlay URL but
      // swap the stored backgroundLocation so the panel beneath reflects
      // the new artifact.
      if (isOverlayPath(location.pathname)) {
        const newBackground = {
          pathname,
          search,
          hash: '',
          state: null,
          key: ARTIFACT_SWITCH_LOCATION_KEY,
        };
        const state: BackgroundLocationState = {
          backgroundLocation:
            newBackground as BackgroundLocationState['backgroundLocation'],
        };
        navigate(
          { pathname: location.pathname, search: location.search },
          { state, replace: true }
        );
        return;
      }

      navigate({ pathname, search });
    },
    [buildHref, navigate, location.pathname, location.search]
  );

  return { navigateToArtifact, buildHref };
}
